import type { DAGData, DAGEdge, DAGNode } from "../../api/artifacts";
import type {
  HumanCard,
  HumanDecisionCard,
  HumanDecisionDetail,
  HumanDecisionStatusEvent,
  HumanEvidenceCard,
  HumanEvidenceDetail,
  HumanJournalCard,
  HumanJournalDetail,
  HumanProvenanceResult,
} from "../../api/humanProvenance";

const V2_HUMAN_KINDS = new Set([
  "decision",
  "decision_evidence",
  "decision_status_event",
  "journal_entry",
]);

export function humanDecisionNodeId(id: string): string {
  return `decision:${id}`;
}

export function humanJournalNodeId(id: string): string {
  return `journal_entry:${id}`;
}

export function humanStatusNodeId(id: string): string {
  return `decision_status_event:${id}`;
}

export function humanEvidenceNodeId(decisionId: string, artifactVersionId: string): string {
  return `decision_evidence:${decisionId}:${artifactVersionId}`;
}

export type HumanNodeRef =
  | { entity_kind: "decision"; id: string }
  | { entity_kind: "journal_entry"; id: string }
  | { entity_kind: "decision_status_event"; id: string }
  | { entity_kind: "decision_evidence"; decision_id: string; artifact_version_id: string };

export function parseHumanNodeId(value: string | null | undefined): HumanNodeRef | null {
  if (!value) return null;
  const parts = value.split(":");
  if (parts[0] === "decision_evidence" && parts.length === 3 && parts[1] && parts[2]) {
    return { entity_kind: "decision_evidence", decision_id: parts[1], artifact_version_id: parts[2] };
  }
  if ((parts[0] === "decision" || parts[0] === "journal_entry" || parts[0] === "decision_status_event")
    && parts.length === 2 && parts[1]) {
    return { entity_kind: parts[0], id: parts[1] };
  }
  return null;
}

function canonicalKind(node: DAGNode): string {
  const metadata = node.metadata;
  return typeof metadata?.canonical_entity_kind === "string"
    ? metadata.canonical_entity_kind
    : node.kind;
}

/**
 * Remove every v2 human record before joining the permission-safe sidecar.
 *
 * This is intentionally unconditional: v2 is a technical transport and its
 * atomic decision/evidence arrays cannot be mixed with a partial human.v1
 * disclosure. A permission-filtered empty sidecar therefore renders no v2
 * human records rather than falling back to a leakier representation.
 */
export function suppressV2HumanNodes(technical: DAGData): DAGData {
  const removed = new Set(
    technical.nodes
      .filter((node) => V2_HUMAN_KINDS.has(canonicalKind(node)))
      .map((node) => node.id),
  );
  if (removed.size === 0) return technical;
  return {
    nodes: technical.nodes.filter((node) => !removed.has(node.id)),
    edges: technical.edges.filter((edge) => !removed.has(edge.parent_id) && !removed.has(edge.child_id)),
  };
}

function cardKey(card: HumanCard): string {
  if (card.entity_kind === "decision_evidence") {
    return humanEvidenceNodeId(card.ref.decision_id, card.ref.artifact_version_id);
  }
  return card.entity_kind === "decision"
    ? humanDecisionNodeId(card.ref.id)
    : humanJournalNodeId(card.ref.id);
}

function actorNode(actorId: string): DAGNode {
  return {
    id: `actor:${actorId}`,
    path: `actor:${actorId}`,
    kind: "actor",
    type: "human",
    node_type: "human",
    record_type: "human",
    semantic_role: "actor",
    name: actorId,
    metadata: {
      semantic_role: "actor",
      human_actor_id: actorId,
      actor_resolution: "id_only",
      human_provenance_contract: "thread.human-provenance.v1",
    },
  };
}

function decisionNode(card: HumanDecisionCard): DAGNode {
  return {
    id: humanDecisionNodeId(card.ref.id),
    path: humanDecisionNodeId(card.ref.id),
    kind: "decision",
    type: "decision",
    node_type: "decision",
    record_type: "decision",
    semantic_role: "decision",
    name: `${card.decision_key} · ${card.technical_claim_name}`,
    state: card.current_status,
    created_at: card.effective_at,
    created_by: card.creator_actor_id,
    metadata: {
      semantic_role: "decision",
      human_record: card,
      human_ref: card.ref,
      human_creator_actor_id: card.creator_actor_id,
      human_technical_claim_ref: card.technical_claim_ref,
      human_provenance_contract: "thread.human-provenance.v1",
    },
  };
}

function evidenceNode(card: HumanEvidenceCard, ordinal?: number): DAGNode {
  const ordinalMetadata = ordinal === undefined ? {} : { human_evidence_ordinal: ordinal };
  return {
    id: humanEvidenceNodeId(card.ref.decision_id, card.ref.artifact_version_id),
    path: humanEvidenceNodeId(card.ref.decision_id, card.ref.artifact_version_id),
    kind: "decision_evidence",
    type: "decision",
    node_type: "decision",
    record_type: "decision",
    semantic_role: "decision_evidence",
    name: card.evidence_name,
    state: card.validity.lifecycle_state,
    created_at: card.effective_at,
    created_by: card.creator_actor_id,
    metadata: {
      semantic_role: "decision_evidence",
      human_record: card,
      human_ref: card.ref,
      human_creator_actor_id: card.creator_actor_id,
      human_evidence_disclosure: ordinal === undefined ? "ordinal_not_disclosed" : "complete",
      ...ordinalMetadata,
      human_provenance_contract: "thread.human-provenance.v1",
    },
  };
}

function journalNode(card: HumanJournalCard): DAGNode {
  return {
    id: humanJournalNodeId(card.ref.id),
    path: humanJournalNodeId(card.ref.id),
    kind: "journal_entry",
    type: "human",
    node_type: "human",
    record_type: "human",
    semantic_role: "journal",
    name: card.content_name,
    state: card.validity.lifecycle_state,
    created_at: card.effective_at,
    created_by: card.author_actor_id,
    metadata: {
      semantic_role: "journal",
      human_record: card,
      human_ref: card.ref,
      human_author_actor_id: card.author_actor_id,
      human_subject_ref: card.subject_ref,
      human_provenance_contract: "thread.human-provenance.v1",
    },
  };
}

function statusNode(
  id: string,
  decisionId: string,
  status: string,
  effectiveAt: string,
  event?: HumanDecisionStatusEvent,
): DAGNode {
  return {
    id: humanStatusNodeId(id),
    path: humanStatusNodeId(id),
    kind: "decision_status_event",
    type: "decision",
    node_type: "decision",
    record_type: "decision",
    semantic_role: "decision_status",
    name: `Status · ${status.split("_").join(" ")}`,
    state: status,
    created_at: effectiveAt,
    created_by: event?.actor_id,
    metadata: {
      semantic_role: "decision_status",
      human_status_event: event ?? null,
      human_status_event_ref: { entity_kind: "decision_status_event", id },
      human_decision_ref: { entity_kind: "decision", id: decisionId },
      ...(event ? { human_status_actor_id: event.actor_id } : {}),
      human_provenance_contract: "thread.human-provenance.v1",
    },
  };
}

function restrictionNode(decisionId: string): DAGNode {
  return {
    id: `decision:${decisionId}:restricted-evidence`,
    path: `decision:${decisionId}:restricted-evidence`,
    kind: "restricted_evidence_marker",
    type: "lifecycle",
    node_type: "lifecycle",
    record_type: "lifecycle",
    semantic_role: "boundary",
    name: "Additional evidence is restricted",
    state: "restricted",
    metadata: {
      semantic_role: "boundary",
      human_restriction_marker: "restricted_evidence_omitted",
      human_decision_ref: { entity_kind: "decision", id: decisionId },
      human_provenance_contract: "thread.human-provenance.v1",
    },
  };
}

interface HumanRecords {
  cards: Map<string, { card: HumanCard; ordinal?: number }>;
  statusEvents: Map<string, HumanDecisionStatusEvent>;
  partialDecisionIds: Set<string>;
}

function humanRecords(result: HumanProvenanceResult): HumanRecords {
  const cards = new Map<string, { card: HumanCard; ordinal?: number }>();
  const statusEvents = new Map<string, HumanDecisionStatusEvent>();
  const partialDecisionIds = new Set<string>();
  const addCard = (card: HumanCard, ordinal?: number) => {
    const key = cardKey(card);
    const current = cards.get(key);
    if (!current || (current.ordinal === undefined && ordinal !== undefined)) cards.set(key, { card, ordinal });
  };
  result.items.forEach((card) => addCard(card));
  const detail = result.detail;
  if (detail?.entity_kind === "decision") {
    addCard(detail);
    detail.status_history.forEach((event) => statusEvents.set(event.ref.id, event));
    if (detail.evidence.disclosure === "complete") {
      detail.evidence.items.forEach((card) => addCard(card, card.ordinal));
    } else {
      partialDecisionIds.add(detail.ref.id);
      detail.evidence.items.forEach((card) => addCard(card));
    }
  } else if (detail?.entity_kind === "decision_evidence") {
    addCard(detail, detail.ordinal ?? undefined);
    addCard(detail.parent_decision);
    if (detail.parent_evidence_disclosure === "partial") partialDecisionIds.add(detail.decision_ref.id);
  } else if (detail?.entity_kind === "journal_entry") {
    addCard(detail);
    if (detail.related_decision) addCard(detail.related_decision);
  }
  return { cards, statusEvents, partialDecisionIds };
}

function addEdge(edges: Map<string, DAGEdge>, source: string, target: string, relationship: string): void {
  if (source === target) return;
  const id = `human:${relationship}:${source}:${target}`;
  edges.set(id, {
    id,
    parent_id: source,
    child_id: target,
    relationship,
    metadata: { human_provenance_contract: "thread.human-provenance.v1" },
  });
}

/**
 * Join the strict human.v1 sidecar to visible technical IDs only.
 * No conversation endpoint, artifact lookup, or v2 human fallback participates.
 */
export function composeHumanProvenanceDag(
  technical: DAGData,
  result: HumanProvenanceResult,
): DAGData {
  const suppressed = suppressV2HumanNodes(technical);
  const records = humanRecords(result);
  if (records.cards.size === 0 && records.statusEvents.size === 0 && records.partialDecisionIds.size === 0) {
    return suppressed;
  }
  const nodes = new Map(suppressed.nodes.map((node) => [node.id, node] as const));
  const edges = new Map(suppressed.edges.map((edge) => [edge.id, edge] as const));
  const ensureNode = (node: DAGNode) => {
    if (!nodes.has(node.id)) nodes.set(node.id, node);
  };
  const ensureActor = (id: string) => ensureNode(actorNode(id));
  const visibleTechnicalId = (kind: "artifact_version" | "operation_run", id: string): string | null => {
    const key = `${kind}:${id}`;
    const node = nodes.get(key);
    return node && canonicalKind(node) === kind ? key : null;
  };

  for (const event of records.statusEvents.values()) {
    ensureActor(event.actor_id);
    ensureNode(statusNode(event.ref.id, event.decision_ref.id, event.status, event.effective_at, event));
  }

  for (const { card, ordinal } of records.cards.values()) {
    if (card.entity_kind === "decision") {
      ensureNode(decisionNode(card));
      ensureActor(card.creator_actor_id);
      const claimId = visibleTechnicalId("artifact_version", card.technical_claim_ref.id);
      if (claimId) addEdge(edges, claimId, humanDecisionNodeId(card.ref.id), "technical_claim_decided_by");
      addEdge(edges, `actor:${card.creator_actor_id}`, humanDecisionNodeId(card.ref.id), "created_decision");
      const currentStatus = records.statusEvents.get(card.current_status_event_ref.id);
      if (currentStatus) {
        addEdge(edges, humanStatusNodeId(currentStatus.ref.id), humanDecisionNodeId(card.ref.id), "current_decision_status");
      }
      continue;
    }
    if (card.entity_kind === "decision_evidence") {
      ensureNode(evidenceNode(card, ordinal));
      ensureActor(card.creator_actor_id);
      const evidenceId = visibleTechnicalId("artifact_version", card.evidence_artifact_version_ref.id);
      if (evidenceId) addEdge(edges, evidenceId, cardKey(card), "evidence_source");
      addEdge(edges, humanDecisionNodeId(card.decision_ref.id), cardKey(card), "supported_by_evidence");
      addEdge(edges, `actor:${card.creator_actor_id}`, cardKey(card), "created_evidence");
      continue;
    }
    ensureNode(journalNode(card));
    ensureActor(card.author_actor_id);
    const contentId = visibleTechnicalId("artifact_version", card.content_artifact_version_ref.id);
    const subjectId = visibleTechnicalId("artifact_version", card.subject_ref.id);
    const publicationId = visibleTechnicalId("operation_run", card.publication_operation_ref.id);
    if (contentId) addEdge(edges, contentId, cardKey(card), "journal_content");
    if (subjectId) addEdge(edges, subjectId, cardKey(card), "documented_by_journal");
    addEdge(edges, `actor:${card.author_actor_id}`, cardKey(card), "authored_journal");
    if (publicationId) addEdge(edges, publicationId, cardKey(card), "published_journal");
    if (card.decision_ref) addEdge(edges, cardKey(card), humanDecisionNodeId(card.decision_ref.id), "documents_decision");
  }

  for (const event of records.statusEvents.values()) {
    addEdge(edges, `actor:${event.actor_id}`, humanStatusNodeId(event.ref.id), "recorded_status");
    addEdge(edges, humanStatusNodeId(event.ref.id), humanDecisionNodeId(event.decision_ref.id), "decision_status_history");
  }
  for (const decisionId of records.partialDecisionIds) {
    const marker = restrictionNode(decisionId);
    ensureNode(marker);
    addEdge(edges, humanDecisionNodeId(decisionId), marker.id, "restricted_evidence_omitted");
  }

  const nodeIds = new Set(nodes.keys());
  return {
    nodes: [...nodes.values()].sort((left, right) => left.id.localeCompare(right.id)),
    edges: [...edges.values()]
      .filter((edge) => nodeIds.has(edge.parent_id) && nodeIds.has(edge.child_id))
      .sort((left, right) => left.id.localeCompare(right.id)),
  };
}

export function humanCardFromNode(node: { metadata: Record<string, unknown> }): HumanCard | null {
  const value = node.metadata.human_record;
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const kind = (value as { entity_kind?: unknown }).entity_kind;
  return kind === "decision" || kind === "decision_evidence" || kind === "journal_entry"
    ? value as HumanCard
    : null;
}

export function humanDetailFromResult(result: HumanProvenanceResult | null):
  HumanDecisionDetail | HumanEvidenceDetail | HumanJournalDetail | null {
  return result?.detail ?? null;
}
